import { ComponentType, ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { NumberParam, useQueryParams } from 'use-query-params';

import Color from 'src/assets/_util.scss';
import { canAccessAdminPage } from 'src/components/AdminPage/AdminPage';
import { ContextMenu } from 'src/components/core/ContextMenu';
import CorticoLogo from 'src/components/core/CorticoLogo';
import {
  AvatarIcon,
  BellIcon,
  ConversationsIcon,
  GearIcon,
  HighlightIcon,
  HomeIcon,
  IconProps,
  LightbulbIcon,
  PlusIcon,
  QuestionCircleIcon,
  RecordIcon,
  SidebarIcon,
  UploadIcon,
} from 'src/components/core/Icon/Icon';
import { HSpacer } from 'src/components/core/Spacer';
import { HStack, VStack } from 'src/components/core/Stack';
import { View } from 'src/components/core/View';
import HeadwayWidget from 'src/components/HeadwayWidget/HeadwayWidget';
import Restrict from 'src/components/Restrict/Restrict';
import authSelectors from 'src/redux/auth/auth-selectors';
import { getActiveCollection } from 'src/util/collections';
import { useLocalStorage } from 'src/util/hooks';
import { createBasicProvider } from 'src/util/provider';
import { TransitionSineOut } from 'src/util/styles';
import {
  getAudioUploadPermission,
  getInsightsRoles,
  getStaffFeaturesPermission,
  hasPermission,
} from 'src/util/user';

import styles from './SideNav.module.scss';

export function SideNav() {
  const { t } = useTranslation();
  const user = useSelector(authSelectors.getUser);
  const [expanded, setExpanded] = useLocalStorage('sideNavExpanded', true);
  const {
    homeLink,
    conversationsLink,
    highlightsLink,
    insightsLink,
    notificationsLink,
    profileLink,
  } = useLinksWithCollectionId();

  return (
    <LocalProvider value={{ expanded }}>
      <nav
        style={{
          height: '100%',
          padding: '12px',
          position: 'relative',
        }}
      >
        <VStack
          sx={{
            height: '100%',
            borderRadius: '20px',
            background: Color.white,
            border: `1px solid ${Color.gray400}`,
            px: '20px',
          }}
        >
          <HStack
            sx={{
              py: '20px',
              alignItems: 'center',
              borderBottom: `1px solid ${Color.gray400}`,
            }}
          >
            <View
              sx={{
                maxWidth: expanded ? '124px' : '0px',
                opacity: expanded ? 1 : 0,
                overflow: 'hidden',
                ...TransitionSineOut,
              }}
            >
              <CorticoLogo color={Color.foraPurpleDark} width={124} />
            </View>

            <HSpacer
              sx={{
                maxWidth: expanded ? '216px' : '0px',
                flexGrow: 1,
                ...TransitionSineOut,
              }}
            />

            <button
              onClick={() => setExpanded(!expanded)}
              style={{
                maxWidth: expanded ? '32px' : '100%',
                width: '100%',
                ...TransitionSineOut,
              }}
              aria-label="toggle sidebar"
              title="toggle sidebar"
            >
              <SidebarIcon color={Color.foraPurpleDark} />
            </button>
          </HStack>

          <VStack
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              alignItems: 'flex-start',
              py: '20px',
              gap: '2px',
              pr: expanded ? '24px' : '0px',
              ...TransitionSineOut,
            }}
          >
            <LinkNavItem
              label={t('main_nav.home')}
              Icon={HomeIcon}
              to={homeLink}
              exact
              pathMatches={['/topics']}
            />
            <LinkNavItem
              label={t('main_nav.conversations')}
              Icon={ConversationsIcon}
              to={conversationsLink}
              pathMatches={['/conversation']}
            />
            <LinkNavItem
              label={t('main_nav.highlights')}
              Icon={HighlightIcon}
              to={highlightsLink}
              pathMatches={['/highlight']}
            />

            <Restrict anyRole={getInsightsRoles(user)}>
              <LinkNavItem
                label={t('main_nav.insights')}
                Icon={LightbulbIcon}
                to={insightsLink}
              />
            </Restrict>

            <Restrict type="custom" customCheck={canAccessAdminPage}>
              <LinkNavItem
                label={t('main_nav.account_settings')}
                Icon={GearIcon}
                to="/admin"
              />
            </Restrict>

            <Restrict
              anyPermission={[
                getAudioUploadPermission('create'),
                getStaffFeaturesPermission('read'),
              ]}
            >
              <CreateButtonNavItem />
            </Restrict>
          </VStack>

          <VStack
            sx={{
              py: '20px',
              borderTop: `1px solid ${Color.gray400}`,
              alignItems: 'flex-start',
              gap: '2px',
              ...TransitionSineOut,
            }}
          >
            <LinkNavItem
              label={t('main_nav.account_help')}
              Icon={QuestionCircleIcon}
              to={{ pathname: 'https://help.cortico.ai/hc/en-us' }}
              target="_blank"
            />
            <LinkNavItem
              label={t('main_nav.notifications')}
              Icon={BellIcon}
              rightItem={<HeadwayWidget style={s.unreadCount(expanded)} />}
              to={notificationsLink}
            />
            <LinkNavItem
              label={user?.first_name ?? ''}
              Icon={UserProfileIcon}
              to={profileLink}
            />
          </VStack>
        </VStack>
      </nav>
    </LocalProvider>
  );
}

// NAV ITEM

function CreateButtonNavItem() {
  const { t } = useTranslation();
  const user = useSelector(authSelectors.getUser);
  const canCreate = hasPermission(user, getAudioUploadPermission('create'));

  const [active, setActive] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const linkProps = {
    className: `${styles.navLink} ${styles.navLinkButton}`,
    onClick: () => setActive(false),
  };

  return !canCreate ? null : (
    <>
      <button
        onClick={() => setActive(!active)}
        style={{ padding: 0, background: 'none', border: 'none' }}
        type="button"
        aria-haspopup="true"
        aria-expanded={active}
        ref={ref}
      >
        <NavItem {...{ active, label: t('main_nav.create'), Icon: PlusIcon }} />
      </button>

      <ContextMenu
        open={active}
        setOpen={setActive}
        anchorRef={ref}
        style={{
          background: Color.gray200,
          border: `1px solid ${Color.gray300}`,
        }}
      >
        <NavLink to="/conversation-upload" {...linkProps}>
          <CreateMenuItem text={t('main_nav.upload')} Icon={UploadIcon} />
        </NavLink>

        <NavLink to="/conversation-record" {...linkProps}>
          <CreateMenuItem
            text={t('main_nav.record')}
            Icon={RecordIcon}
            noDivider
          />
        </NavLink>
      </ContextMenu>
    </>
  );
}

function LinkNavItem({
  label,
  Icon,
  rightItem,
  exact,
  pathMatches,
  ...navLinkProps
}: NavLinkProps &
  Omit<NavItemProps, 'active'> & {
    pathMatches?: string[];
  }) {
  const { pathname: path } = useLocation();

  // remove any params so matching works
  const { to } = navLinkProps;
  const link = typeof to === 'string' ? to.split('?')[0] : 'null';

  const active =
    (exact ? path === link : path.includes(link)) ||
    (pathMatches ?? []).some((p) => path.includes(p));

  return (
    <NavLink {...navLinkProps} className={styles.navLink}>
      <NavItem
        active={active}
        label={label}
        Icon={Icon}
        rightItem={rightItem}
      />
    </NavLink>
  );
}

function NavItem({
  label,
  Icon,
  active,
  rightItem = null,
}: {
  label: string;
  Icon: ComponentType<IconProps>;
  active: boolean;
  rightItem?: ReactNode;
}) {
  const { expanded } = useLocalContext();
  const color = s.navItemColor(active);

  return (
    <HStack sx={{ ...s.navItem(active), ...color }}>
      <Icon size={20} />
      <Typography
        sx={{
          userSelect: 'none',
          maxWidth: expanded ? '176px' : '0px',
          opacity: expanded ? 1 : 0,
          paddingLeft: expanded ? '8px' : 0,
          fontWeight: 600,
          lineClamp: 1,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          textWrapMode: 'nowrap',
          ...TransitionSineOut,
        }}
      >
        {label}
      </Typography>
      {rightItem}
    </HStack>
  );
}

// CREATE MENU

function CreateMenuItem({
  text,
  Icon = () => null,
  noDivider = false,
}: {
  text?: string;
  Icon?: ComponentType<IconProps>;
  noDivider?: boolean;
}) {
  return (
    <HStack
      sx={{
        p: '16px',
        gap: '24px',
        justifyContent: 'space-between',
        color: Color.foraPurpleDark,
        borderBottom: noDivider ? '' : `1px solid ${Color.gray300}`,
        fontWeight: 600,
      }}
    >
      {text}
      <Icon size={20} />
    </HStack>
  );
}

// USER PROFILE

function UserProfileIcon(iconProps: IconProps) {
  const { profile_image } = useSelector(authSelectors.getUser);

  return (
    <View>
      {profile_image ? (
        <img
          src={profile_image}
          alt="Profile Image"
          style={{
            width: '20px',
            maxWidth: '20px',
            maxHeight: '20px',
            borderRadius: '100%',
            border: `1.5px solid ${Color.foraPurple}`,
          }}
        />
      ) : (
        <AvatarIcon {...iconProps} />
      )}
    </View>
  );
}

// HELPERS

function useLinksWithCollectionId() {
  const user = useSelector(authSelectors.getUser);
  const [query] = useQueryParams({ c: NumberParam });
  const activeCollection = getActiveCollection(query.c, user);

  const withCollection =
    activeCollection && !activeCollection.is_all_collection_option;

  const makeLink = (path: string) =>
    withCollection ? `${path}?c=${activeCollection.id}` : path;

  return {
    homeLink: makeLink('/'),
    conversationsLink: makeLink('/conversations'),
    highlightsLink: makeLink('/highlights'),
    insightsLink: makeLink('/insights'),
    notificationsLink: makeLink('/notifications'),
    profileLink: makeLink('/settings'),
  };
}

// STYLE

const s = {
  navItem: (active: boolean) => ({
    textDecoration: 'none',
    background: Color[active ? 'foraPurple' : 'white'],
    overflow: 'hidden',
    borderRadius: '12px',
    flexShrink: 0,
    padding: '12px 14px 12px 12px',
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
    ...TransitionSineOut,
  }),
  navItemColor: (active: boolean) => ({
    color: Color[active ? 'white' : 'foraPurpleDark'],
  }),
  unreadCount: (expanded: boolean) => ({
    position: expanded ? ('initial' as const) : ('absolute' as const),
    top: expanded ? 0 : '2px',
    right: expanded ? 0 : '9px',
    display: 'flex',
    justifyContent: 'center',
    flexGrow: 1,
  }),
};

// DEFINITIONS

const [LocalProvider, useLocalContext] = createBasicProvider<{
  expanded: boolean;
}>();

type NavItemProps = Parameters<typeof NavItem>[0];
